import { useState, useRef } from 'react'
import { Link } from 'react-router'
import {
  Download,
  Upload,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  CreditCard,
  Loader2,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { useSubscription, useCustomerPortal } from '@/hooks/useSubscription'
import { getStatusBadgeStyles } from '@/components/subscription/StripeBillingPage'
import {
  useDataExport,
  useDataImport,
  downloadAsJson,
  readJsonFile,
  validateImportData,
  type TeamDataExport,
  type ImportResult,
} from '@/hooks/useDataExportImport'
import { UsageIndicator } from '@/components/subscription/UsageGate'
import { formatLongDate } from '@/lib/date'
import { useLabels } from '@/lib/terminology'
import { TerminologySettings } from '@/components/settings/TerminologySettings'

export function SettingsPage() {
  const { user, profile, membership } = useAuth()
  const labels = useLabels()
  const {
    subscription,
    isLoading: subscriptionLoading,
    isTrialActive,
    isTrialExpired,
    isPaid,
    trialDaysRemaining,
  } = useSubscription()
  const portal = useCustomerPortal()
  const dataExport = useDataExport()
  const dataImport = useDataImport()

  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const [exportSuccess, setExportSuccess] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const teamId = membership?.team_id

  const handleExport = async () => {
    if (!teamId) return
    setExportSuccess(false)
    try {
      const data = await dataExport.mutateAsync(teamId)
      const teamName = membership?.teams?.name || 'floaty'
      const date = new Date().toISOString().split('T')[0]
      downloadAsJson(data, `${teamName}-export-${date}.json`)
      setExportSuccess(true)
      setTimeout(() => setExportSuccess(false), 3000)
    } catch (err) {
      console.error('Export failed:', err)
    }
  }

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !teamId) return

    setImportResult(null)
    setImportError(null)

    try {
      const data = await readJsonFile(file)

      if (!validateImportData(data)) {
        setImportError('Invalid file format. Please select a valid Floaty export file.')
        return
      }

      const result = await dataImport.mutateAsync({ teamId, data: data as TeamDataExport })
      setImportResult(result)
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed')
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const statusStyles = subscription ? getStatusBadgeStyles(subscription.status) : null

  return (
    <div className="p-6 max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">Settings</h1>
        <p className="text-zinc-500 mt-1">Manage your account, subscription, and data</p>
      </div>

      {/* Profile Section */}
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 mb-6">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-4">Profile</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Name
            </label>
            <input
              type="text"
              defaultValue={profile?.name || ''}
              className="w-full px-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Email
            </label>
            <input
              type="email"
              defaultValue={profile?.email || user?.email || ''}
              className="w-full px-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
              readOnly
            />
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 mb-6">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-4">Team</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Team Name
            </label>
            <input
              type="text"
              defaultValue={membership?.teams?.name || ''}
              className="w-full px-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Role
            </label>
            <input
              type="text"
              defaultValue={membership?.role || ''}
              className="w-full px-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 capitalize"
              readOnly
            />
          </div>
        </div>
      </div>

      {/* Terminology Settings */}
      <TerminologySettings />

      {/* Subscription Section */}
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Subscription</h2>
          <CreditCard className="w-5 h-5 text-zinc-400" />
        </div>

        {subscriptionLoading ? (
          <div className="flex items-center gap-2 text-zinc-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading subscription...
          </div>
        ) : !subscription ? (
          <div className="text-center py-4">
            <p className="text-zinc-600 dark:text-zinc-400 mb-4">
              No subscription found. Start your free trial to get going!
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              View Plans
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Status and Plan */}
            <div className="flex items-center gap-3">
              <span className="text-zinc-700 dark:text-zinc-300 font-medium">
                {subscription.status === 'trial' ? 'Free Trial' : 'Paid Plan'}
              </span>
              {statusStyles && (
                <span
                  className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${statusStyles.bg} ${statusStyles.text}`}
                >
                  {statusStyles.label}
                </span>
              )}
            </div>

            {/* Trial Warning */}
            {isTrialExpired && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Your trial has expired. Upgrade to continue creating content.
                  </p>
                </div>
              </div>
            )}

            {/* Trial Days Remaining */}
            {isTrialActive && trialDaysRemaining !== null && (
              <p className="text-sm text-cyan-600 dark:text-cyan-400">
                {trialDaysRemaining} days remaining in trial
              </p>
            )}

            {/* Next Billing Date */}
            {isPaid && subscription.current_period_end && (
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Next billing date: {formatLongDate(subscription.current_period_end)}
              </p>
            )}

            {/* Usage Indicators */}
            <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700">
              <h3 className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">
                Usage
              </h3>
              <div className="grid gap-4">
                <UsageIndicator type="recording" />
                <UsageIndicator type="guest" />
                <UsageIndicator type="series" />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="pt-4 flex flex-wrap gap-3">
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm bg-orange-500 hover:bg-orange-600 text-white transition-colors"
              >
                {isPaid ? 'Change Plan' : 'Upgrade'}
              </Link>

              {isPaid && subscription.stripe_customer_id && (
                <button
                  onClick={() => portal.mutate()}
                  disabled={portal.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
                >
                  {portal.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4" />
                      Manage Billing
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Data Import/Export Section */}
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
          Data Import & Export
        </h2>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
          Export your team's data as JSON for backup or migration, or import data from a previous export.
        </p>

        {/* Export Section */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
            Export Data
          </h3>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">
            Downloads all your {labels.collections.toLowerCase()}, {labels.recordings.toLowerCase()}, {labels.guests.toLowerCase()}, and related data as a JSON file.
          </p>
          <button
            onClick={handleExport}
            disabled={dataExport.isPending || !teamId}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50"
          >
            {dataExport.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Export JSON
              </>
            )}
          </button>

          {exportSuccess && (
            <div className="mt-3 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
              <CheckCircle className="w-4 h-4" />
              Export downloaded successfully!
            </div>
          )}

          {dataExport.isError && (
            <div className="mt-3 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertTriangle className="w-4 h-4" />
              Export failed: {dataExport.error?.message || 'Unknown error'}
            </div>
          )}
        </div>

        {/* Import Section */}
        <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700">
          <h3 className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
            Import Data
          </h3>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">
            Import data from a previously exported JSON file. This will add the data to your current team.
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            className="hidden"
          />

          <button
            onClick={handleImportClick}
            disabled={dataImport.isPending || !teamId}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
          >
            {dataImport.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Import JSON
              </>
            )}
          </button>

          {/* Import Warning */}
          <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Importing data will create new records. Duplicates may be created if you import the
                same data twice. Team-level production settings will be replaced with imported
                values.
              </p>
            </div>
          </div>

          {/* Import Result */}
          {importResult && (
            <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-green-700 dark:text-green-300">
                  <p className="font-medium mb-1">Import completed successfully!</p>
                  <ul className="text-xs space-y-0.5">
                    <li>{importResult.seriesImported} {labels.collections.toLowerCase()}</li>
                    <li>{importResult.episodesImported} {labels.recordings.toLowerCase()}</li>
                    <li>{importResult.guestsImported} {labels.guests.toLowerCase()}</li>
                    <li>{importResult.appearancesImported} {labels.appearances.toLowerCase()}</li>
                    <li>{importResult.communicationsImported} communications</li>
                    <li>{importResult.prepChecklistsImported} prep checklists</li>
                    <li>{importResult.postProductionImported} post-production items</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Import Error */}
          {importError && (
            <div className="mt-3 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertTriangle className="w-4 h-4" />
              {importError}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
